<!-- User Profile -->
@if (user) {
  <ui-card className="mb-6" [shadow]="false">
    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div class="flex items-center gap-4">
        <div class="avatar">
          <div class="w-20 h-20 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
            <img [src]="user.picture_url" alt="{{ user.nickname }}" useImageProxy />
          </div>
        </div>
        <div>
          <h3 class="text-2xl font-bold">{{ user.nickname }}</h3>
          <div class="flex items-center gap-2 text-sm opacity-70 mt-1">
            <fa-icon [icon]="faEnvelope"></fa-icon>
            <span>{{ user.email }}</span>
          </div>
        </div>
      </div>
    </div>
  </ui-card>
}

<!-- Add Household Modal (ui-modal) -->
<ui-modal #addHouseholdModal title="Add Household" size="md">
  @if (householdError) {
    <div class="alert alert-error mb-4">
      <span>{{ householdError }}</span>
    </div>
  }

  <form [formGroup]="householdForm" class="space-y-4" (ngSubmit)="saveHousehold()">
    <label class="label cursor-pointer justify-start gap-3">
      <input type="checkbox" class="toggle toggle-primary" formControlName="isDefault" />
      <span class="label-text">Default household</span>
    </label>

    <div class="form-control">
      <label class="label" for="household-name">
        <span class="label-text">Name</span>
      </label>
      <input
        id="household-name"
        type="text"
        class="input input-bordered w-full"
        formControlName="name"
        placeholder="Household name"
      />
      @if (householdForm.get('name')?.invalid && (householdForm.get('name')?.dirty || householdForm.get('name')?.touched)) {
        <label class="label">
          <span class="label-text-alt text-error">Please enter a name.</span>
        </label>
      }
    </div>
  </form>

  <div modal-footer class="w-full flex justify-end gap-2">
    <ui-button [variant]="'secondary'" [outline]="true" (clicked)="closeHouseholdModal()" [disabled]="householdSaving">
      Close
    </ui-button>
    <ui-button
      [variant]="'primary'"
      (clicked)="saveHousehold()"
      [disabled]="householdSaving || householdForm.invalid"
      [loading]="householdSaving"
    >
      Save
    </ui-button>
  </div>
</ui-modal>

<!-- Households -->
<ui-card className="mb-6" [shadow]="false">
  <div class="flex items-start justify-between gap-4 mb-4">
    <div>
      <h3 class="text-lg font-semibold">Households</h3>
      <p class="text-sm opacity-70 mt-1">
        Manage your households. A household has one or more stores, where items can be in stock.
        A household can be shared with other users.
      </p>
    </div>
    <ui-button [variant]="'primary'" [outline]="true" size="sm" (clicked)="openNewHousehold()">
      <fa-icon [icon]="faEdit" class="mr-1"></fa-icon>
      <span>Add</span>
    </ui-button>
  </div>

  <ui-list [variant]="'bordered'" [boxed]="true" [compact]="true">
    @for (h of households; track h) {
      <ui-list-item>
        <div class="grid grid-cols-[1fr_5.5rem_auto] items-center gap-4 w-full">
          <a
            class="font-medium text-base-content no-underline hover:text-primary transition-colors truncate min-w-0"
            [routerLink]="['/households/', h.id, 'manage']"
          >
            {{ h.name }}
          </a>

          <div class="flex justify-start">
            @if (h.is_owner) {
              <ui-badge [variant]="'neutral'" size="sm">Owner</ui-badge>
            }
          </div>

          <div class="flex justify-end">
            <input
              type="checkbox"
              class="toggle toggle-primary toggle-sm"
              [checked]="h.is_default"
              [disabled]="h.is_default || settingDefaultHouseholdId === h.id"
              (change)="setDefaultHousehold(h, $event)"
              [attr.aria-label]="'Set ' + h.name + ' as default household'"
            />
          </div>
        </div>
      </ui-list-item>
    }

    @if (!households || households.length === 0) {
      <div class="p-4 text-sm opacity-60">No households yet.</div>
    }
  </ui-list>
</ui-card>

<!-- Push Notifications -->
<ui-card className="mb-6" [shadow]="false">
  <div class="flex items-start justify-between gap-4 mb-4">
    <div>
      <h3 class="text-lg font-semibold">Notifications</h3>
      <p class="text-sm opacity-70 mt-1">
        Devices registered to receive push notifications for your account.
        Send a test notification to check that delivery works.
      </p>
      <p class="text-xs opacity-50 mt-1">
        "Send test reminder" shows a consumption reminder right now — click it to open Quick check.
        Nothing in your stock changes.
      </p>
      @if (showFakeDevices) {
        <p class="text-xs opacity-50 mt-1">
          Testing only: add a fake device to exercise push delivery without a physical device.
        </p>
      }
    </div>
    <div class="flex items-center gap-2 shrink-0">
      @if (showFakeDevices) {
        <ui-button
          [variant]="'neutral'"
          [outline]="true"
          size="sm"
          (clicked)="addFakeDevice()"
          [disabled]="addingFake"
          [loading]="addingFake"
        >
          <span>Add fake device</span>
        </ui-button>
      }
      <ui-button
        [variant]="'primary'"
        [outline]="true"
        size="sm"
        (clicked)="sendTestReminder()"
        [disabled]="sendingReminder"
        [loading]="sendingReminder"
        title="Shows a reminder now and opens Quick check when you click it"
      >
        <fa-icon [icon]="faBell" class="mr-1"></fa-icon>
        <span>Send test reminder</span>
      </ui-button>
      <ui-button
        [variant]="'neutral'"
        [outline]="true"
        size="sm"
        (clicked)="sendTestNotification()"
        [disabled]="sendingTest || !devices || devices.length === 0"
        [loading]="sendingTest"
        title="Sends a plain push to your registered devices"
      >
        <span>Send test push</span>
      </ui-button>
    </div>
  </div>

  <ui-list [variant]="'bordered'" [boxed]="true" [compact]="true">
    @for (d of devices; track d.id) {
      <ui-list-item>
        <div class="grid grid-cols-[1fr_auto] items-center gap-4 w-full">
          <div class="min-w-0">
            <div class="font-medium truncate">
              {{ d.platform }} · …{{ d.token_suffix }}
              @if (d.app_version) {
                <span class="opacity-60 font-normal">v{{ d.app_version }}</span>
              }
            </div>
            <div class="text-xs opacity-60 mt-0.5">
              Last seen {{ d.last_seen_at | date:'short' }}
            </div>
          </div>
          <div class="flex items-center justify-end gap-2">
            <ui-badge [variant]="d.environment === 'production' ? 'success' : 'neutral'" size="sm">
              {{ d.environment }}
            </ui-badge>
            @if (isFakeDevice(d)) {
              <ui-button
                [variant]="'danger'"
                [outline]="true"
                size="sm"
                (clicked)="removeDevice(d)"
                [disabled]="removingId === d.id"
                [loading]="removingId === d.id"
                aria-label="Remove fake device"
              >
                <fa-icon [icon]="faTrash"></fa-icon>
              </ui-button>
            }
          </div>
        </div>
      </ui-list-item>
    }

    @if (devicesLoading) {
      <div class="p-4 text-sm opacity-60">Loading devices…</div>
    } @else if (!devices || devices.length === 0) {
      <div class="p-4 text-sm opacity-60">No registered devices yet.</div>
    }
  </ui-list>

  <!-- Notification types (opt-out) -->
  <div class="mt-6">
    <h4 class="text-sm font-semibold opacity-80 mb-2">Notification types</h4>
    <p class="text-xs opacity-60 mb-3">
      Choose which kinds of notifications you want to receive. All are on by default.
    </p>

    <ui-list [variant]="'bordered'" [boxed]="true" [compact]="true">
      @for (pref of preferences; track pref.key) {
        <ui-list-item>
          <div class="grid grid-cols-[1fr_auto] items-center gap-4 w-full">
            <div class="min-w-0">
              <div class="font-medium">{{ pref.label }}</div>
              <div class="text-xs opacity-60 mt-0.5">{{ pref.description }}</div>
            </div>
            <div class="flex justify-end">
              <input
                type="checkbox"
                class="toggle toggle-primary toggle-sm"
                [checked]="pref.enabled"
                [disabled]="savingPreferenceKey === pref.key"
                (change)="togglePreference(pref, $any($event.target).checked)"
                [attr.aria-label]="'Toggle ' + pref.label"
              />
            </div>
          </div>
        </ui-list-item>
      }

      @if (preferencesLoading) {
        <div class="p-4 text-sm opacity-60">Loading preferences…</div>
      } @else if (!preferences || preferences.length === 0) {
        <div class="p-4 text-sm opacity-60">No notification types available.</div>
      }
    </ui-list>
  </div>
</ui-card>

<!-- Backend Info -->
@if (apiInfo) {
  <ui-card className="mb-6" [shadow]="false">
    <h3 class="text-lg font-semibold mb-4">Backend Info</h3>
    <ui-list [variant]="'default'" [boxed]="false" [compact]="true">
      @for (e of ($any(apiInfo) | keyvalue); track e.key) {
        <ui-list-item>
          <div class="flex justify-between items-center w-full">
            <span class="opacity-70">{{ e.key }}</span>
            <span class="font-semibold">{{ e.value }}</span>
          </div>
        </ui-list-item>
      }
    </ui-list>
  </ui-card>
}
